import { type TypedDocumentNode, gql } from '@apollo/client';

import { AlgoliaCardIndexesName } from '@sorare/core/src/contexts/config';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import type { useSortSecondaryOfferSlugs_anyCard } from './__generated__/useSortSecondaryOfferSlugs.graphql';

type Props = {
  slugs: string[];
  anyCards: useSortSecondaryOfferSlugs_anyCard[];
  cardIndex: AlgoliaCardIndexesName;
};

export const useSortSecondaryOfferSlugs = ({
  slugs,
  anyCards = [],
  cardIndex,
}: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();

  const sortedSlugs = [...slugs]?.sort((a, b) => {
    const cardA = anyCards.find(card => card.slug === a);
    const cardB = anyCards.find(card => card.slug === b);

    if (cardA?.liveSingleSaleOffer && cardB?.liveSingleSaleOffer) {
      const priceA = toMonetaryAmount(
        cardA.liveSingleSaleOffer.receiverSide.amounts
      );
      const priceB = toMonetaryAmount(
        cardB.liveSingleSaleOffer.receiverSide.amounts
      );

      if (cardIndex === 'Cards On Sale Lowest Price') {
        return priceA.eur - priceB.eur;
      }
      if (cardIndex === 'Cards On Sale Highest Price') {
        return priceB.eur - priceA.eur;
      }
    }
    return 0;
  });
  return sortedSlugs;
};

useSortSecondaryOfferSlugs.fragments = {
  anyCard: gql`
    fragment useSortSecondaryOfferSlugs_anyCard on AnyCardInterface {
      slug
      liveSingleSaleOffer {
        id
        receiverSide {
          id
          amounts {
            ...MonetaryAmountFragment_monetaryAmount
          }
        }
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<useSortSecondaryOfferSlugs_anyCard>,
};
